import { RECALL_EVENT_COPY } from '#app/routes/mocra+/_form+/components/recall-events/constants.ts'
import {
	RECALL_EVENT_OUTCOME_OPTIONS,
	RECALL_EVENT_TYPE_OPTIONS,
} from '#app/routes/mocra+/_form+/intake-options.ts'

export type RecallEventTypeValue =
	(typeof RECALL_EVENT_TYPE_OPTIONS)[number]['value']
export type RecallEventOutcomeValue =
	(typeof RECALL_EVENT_OUTCOME_OPTIONS)[number]['value']

export type RecallEventDraft = {
	type: RecallEventTypeValue
	year: string
	product: string
	outcome: RecallEventOutcomeValue
}

export type RecallEventDraftErrors = Partial<
	Record<keyof RecallEventDraft, string>
>

export const RECALL_EVENT_TYPE_MOCK: RecallEventTypeValue = 'mock_recall'
export const RECALL_EVENT_OUTCOME_FOR_MOCK: RecallEventOutcomeValue =
	'mock_completed'

const DEFAULT_TYPE =
	RECALL_EVENT_TYPE_OPTIONS[0]?.value ?? RECALL_EVENT_TYPE_MOCK
const DEFAULT_OUTCOME =
	RECALL_EVENT_OUTCOME_OPTIONS[0]?.value ?? RECALL_EVENT_OUTCOME_FOR_MOCK
const CURRENT_YEAR = new Date().getFullYear().toString()
const YEAR_PATTERN = /^\d{4}$/

export function createRecallEventDraft(
	overrides: Partial<RecallEventDraft> = {},
): RecallEventDraft {
	return {
		type: DEFAULT_TYPE,
		year: CURRENT_YEAR,
		product: '',
		outcome: DEFAULT_OUTCOME,
		...overrides,
	}
}

export function sanitizeRecallEventDraft(
	draft: RecallEventDraft,
): RecallEventDraft {
	const type = isRecallEventTypeValue(draft.type) ? draft.type : DEFAULT_TYPE
	const outcome = isRecallEventOutcomeValue(draft.outcome)
		? draft.outcome
		: DEFAULT_OUTCOME
	return {
		type,
		outcome,
		year: draft.year.trim(),
		product: draft.product.trim(),
	}
}

export function collectRecallEventErrors(
	draft: RecallEventDraft,
): RecallEventDraftErrors {
	const sanitized = sanitizeRecallEventDraft(draft)
	const errors: RecallEventDraftErrors = {}
	if (!YEAR_PATTERN.test(sanitized.year)) {
		errors.year = RECALL_EVENT_COPY.errors.yearInvalid
	}
	if (sanitized.product.length === 0) {
		errors.product = RECALL_EVENT_COPY.errors.productRequired
	}
	return errors
}

export function recallEventCardTitle(draft: RecallEventDraft): string {
	const sanitized = sanitizeRecallEventDraft(draft)
	const typeLabel = resolveRecallEventTypeLabel(sanitized.type)
	const parts = [typeLabel]
	if (sanitized.year.length > 0) {
		parts.push(sanitized.year)
	}
	return parts.join(' — ')
}

export function formatRecallEventSummary(draft: RecallEventDraft): string {
	const sanitized = sanitizeRecallEventDraft(draft)
	const outcomeLabel = resolveRecallEventOutcomeLabel(sanitized.outcome)
	const productLine =
		sanitized.product.length > 0 ? sanitized.product : 'Product not documented'
	return `${productLine} • Outcome: ${outcomeLabel}`
}

export function resolveRecallEventTypeLabel(
	value: RecallEventTypeValue,
): string {
	return (
		RECALL_EVENT_TYPE_OPTIONS.find((option) => option.value === value)?.label ??
		value
	)
}

export function resolveRecallEventOutcomeLabel(
	value: RecallEventOutcomeValue,
): string {
	return (
		RECALL_EVENT_OUTCOME_OPTIONS.find((option) => option.value === value)
			?.label ?? value
	)
}

export function isRecallEventTypeValue(
	value: unknown,
): value is RecallEventTypeValue {
	return RECALL_EVENT_TYPE_OPTIONS.some((option) => option.value === value)
}

export function isRecallEventOutcomeValue(
	value: unknown,
): value is RecallEventOutcomeValue {
	return RECALL_EVENT_OUTCOME_OPTIONS.some((option) => option.value === value)
}

export function normalizeRecallEventDraft(
	input: unknown,
): RecallEventDraft | null {
	if (!input || typeof input !== 'object') return null
	const record = input as Record<string, unknown>
	const draft = createRecallEventDraft({
		type: isRecallEventTypeValue(record.type) ? record.type : DEFAULT_TYPE,
		outcome: isRecallEventOutcomeValue(record.outcome)
			? record.outcome
			: DEFAULT_OUTCOME,
		year: typeof record.year === 'string' ? record.year : '',
		product: typeof record.product === 'string' ? record.product : '',
	})
	const errors = collectRecallEventErrors(draft)
	if (Object.keys(errors).length > 0) return null
	return sanitizeRecallEventDraft(draft)
}

export function enforceMockDefaults(draft: RecallEventDraft): RecallEventDraft {
	const sanitized = sanitizeRecallEventDraft(draft)
	return {
		...sanitized,
		type: RECALL_EVENT_TYPE_MOCK,
		outcome: RECALL_EVENT_OUTCOME_FOR_MOCK,
	}
}
